import React from "react";
import { Document, Page, View, Text, Image, usePDF, StyleSheet, Link } from "@react-pdf/renderer";
import { usePDFContext } from "../../context/PDFContext";
import { ChairModel, useChair } from "../../context/ChairContext";
import { ChairVariantInfo } from "../../api/hooks/useChairs";
import { calculatePrice, plausibleEvent, toLocalNumber } from "../../utilities/helper";
import { Loader } from "../Loader";
import { useDebounceEffect } from "ahooks";

import { Buffer } from "buffer";

if (typeof window !== "undefined") {
    window.Buffer = window.Buffer || Buffer;
}

type PDFDocumentProps = {
    imageBlob: string;
    chairInfo: ChairVariantInfo;
    chair: ChairModel;
};

const styles = StyleSheet.create({
    page: { backgroundColor: "#e1ebdc", padding: "30px", fontFamily: "Euclid", color: "black", fontSize: "12px" },
    h2: {
        marginBottom: "20px",
        color: "#004c3f",
        fontSize: "20px",
        fontFamily: "biotif",
    },
    info: { margin: "0", padding: "0" },
    wide: { display: "flex", justifyContent: "space-between" },
    button: { padding: "8px 16px", border: "2px solid #004c3f", color: "#004c3f", width: "200px", textAlign: "center" },
});

export const PDFDocument = (props: PDFDocumentProps) => {
    return (
        <Document title={props.chairInfo.parent.chairName} author={"meinstuhl.at"}>
            <Page size="A4" style={styles.page}>
                <View>
                    <Text style={styles.h2}>
                        {props.chairInfo.parent.chairName} {props.chairInfo.variantName}
                    </Text>
                    <Text style={styles.info}>{props.chairInfo.measurements.replace(/ cm/g, "cm")}</Text>
                    <Text style={styles.info}>
                        {props.chairInfo.fabricLFM ? `Stoff: ${props.chairInfo.fabricLFM}lfm` : ""}
                        {props.chairInfo.fabricLFM && props.chairInfo.leatherM2 ? " | " : ""}
                        {props.chairInfo.leatherM2 ? `Leder: ${props.chairInfo.leatherM2}m²` : ""}
                    </Text>
                    <Text> </Text>
                    <View style={[styles.info, styles.wide]}>
                        <Text>{`Gestell: ${props.chair.frame?.name ?? ""} `}</Text>
                        <Text>{` + ${toLocalNumber(props.chair.frame?.price ?? 0)}`}</Text>
                    </View>
                    <View style={[styles.info, styles.wide]}>
                        <Text>{`Verarbeitung: ${props.chair.frame?.texture?.name ?? ""} `}</Text>
                        <Text>{` + ${toLocalNumber(props.chair.frame?.texture?.cost ?? 0)}`}</Text>
                    </View>
                    <View style={[styles.info, styles.wide]}>
                        <Text>{`Bezug: ${props.chair.cover?.className ?? ""} | ${
                            props.chair.cover?.catalog?.name ?? ""
                        } | ${props.chair.cover?.color?.name ?? ""} `}</Text>
                        <Text>{` + ${toLocalNumber(props.chair.cover?.price ?? 0)}`}</Text>
                    </View>
                    <View style={[styles.info, styles.wide]}>
                        <Text>{`Zubehör: ${props.chair.extra?.name ?? ""} `}</Text>
                        <Text>{` + ${toLocalNumber(props.chair.extra?.price ?? 0)}`}</Text>
                    </View>
                    <Image src={props.imageBlob} style={{ height: "400pt", objectFit: "cover" }} />
                </View>
                <View>
                    <View style={[styles.h2, styles.wide]}>
                        <Text>{`${toLocalNumber(calculatePrice(props.chair))}/Stk. `}</Text>
                        <Text style={{ fontSize: "10px" }}>exkl. MwSt</Text>
                    </View>
                    <Link src={`https://meinstuhl.at?ref=pdf`} style={styles.button}>
                        Zum Konfigurator
                    </Link>
                </View>
            </Page>
        </Document>
    );
};

export const PDFButton = () => {
    const imageBlob = usePDFContext((state) => state.imageBlob);
    const chairInfo = usePDFContext((state) => state.chairInfo);
    const { chair } = useChair();

    // 1. Initialize with a valid but empty document. NEVER null.
    // This stops the internal reconciler from crashing when reading props.
    const [pdfInstance, update] = usePDF({
        document: (
            <Document>
                <Page size="A4" />
            </Document>
        ),
    });

    useDebounceEffect(
        () => {
            // 2. Only generate and update the real document when we have actual data
            if (chairInfo && imageBlob) {
                try {
                    update(<PDFDocument imageBlob={imageBlob} chairInfo={chairInfo} chair={chair} />);
                } catch (e) {
                    console.error("PDF Update failed", e);
                }
            }
        },
        [imageBlob, chairInfo, chair], // Watch the direct data, bypassing the old useMemo
        { wait: 1000 },
    );

    return (
        <button
            // Disable the button until the data is ready and the PDF is generated
            disabled={pdfInstance.loading || !chairInfo || !imageBlob || !pdfInstance.url}
            className={"button secondary"}
            onClick={() => {
                if (!chairInfo || !pdfInstance.url) return;
                plausibleEvent("pdf-export", `${chairInfo.parent.chairName} ${chairInfo.variantName}`);
                window.open(pdfInstance.url, "_blank");
            }}
        >
            {pdfInstance.loading ? <Loader /> : "PDF Exportieren"}
        </button>
    );
};
